import { messageOf } from '../lib/errors'
import { myLanes } from '../lib/support'
import { useEffect, useState } from 'react'
import type { Me } from '../lib/me'
import { can, canGallery } from '../lib/me'
import { canReachShopAdmin } from '../lib/shop'
import { supabase } from '../lib/supabase'
import { AppShell } from '../components/AppShell'
import { Card } from '../components/Card'
import { QueueItem } from '../components/moderation/QueueItem'
import {
  loadModerationQueue,
  resolveModeration,
  type ModerationAction,
  type ModerationItem,
} from '../lib/moderation'

/**
 * The moderator review queue — FED-11.
 *
 * ── WHY THIS IS ITS OWN SCREEN ──────────────────────────────────────────────
 *
 * Same argument Admin.tsx makes and for a sharper reason. Moderating is a
 * judgement about a colleague's speech, and 021 split `moderator` out of
 * `comms_admin` precisely to keep that act separate from publishing. Putting
 * the queue in a corner of the feed would re-merge in the interface what the
 * permission model was careful to separate — and it would put a blocked post's
 * full text on the screen everybody opens to read the company's news.
 *
 * It is the app's third route, which App.tsx's own comment says is the moment
 * to revisit the nine-line hash router. Revisited: three flat routes with no
 * params, no nesting and no data loading in the route layer. A router library
 * would add a dependency, a bundle and a set of conventions in service of one
 * `switch`, and OPS-16 says Wave 1 must not fetch anything at runtime anyway.
 * Reconsider at the first route that needs a parameter — a permalink to a
 * single post will be it.
 *
 * ── THE LIST RELOADS AFTER EVERY ACTION, ON PURPOSE ─────────────────────────
 *
 * Not an optimistic splice. resolve_moderation writes the content and the
 * verdict in one transaction and 028c is the worked example of that pair going
 * wrong silently — a zero-row UPDATE that returned success and published
 * nothing. If the local list is edited on the assumption the call did what it
 * said, a failure of that shape looks exactly like a success. Re-reading the
 * queue means the screen shows what the database actually holds.
 */
export function Moderation({ me }: { me: Me }) {
  const [items, setItems] = useState<ModerationItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const canModerate = can(me, 'content.moderate')

  async function refresh() {
    try {
      setItems(await loadModerationQueue())
      setError(null)
    } catch (e) {
      setError(messageOf(e))
    }
  }

  useEffect(() => {
    let live = true
    loadModerationQueue()
      .then((q) => live && setItems(q))
      .catch((e: Error) => live && setError(e.message))
    return () => {
      live = false
    }
  }, [])

  async function act(verdictId: string, action: ModerationAction, note?: string) {
    setBusy(verdictId)
    setError(null)
    try {
      await resolveModeration(verdictId, action, note)
      await refresh()
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(null)
    }
  }

  const shell = (children: React.ReactNode) => (
    <AppShell
      current="Moderation"
      canAdmin={can(me, 'admin.roles')}
      canShop={canReachShopAdmin(me)}
      canModerate={canModerate}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery(me)}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      queueCount={items?.length ?? 0}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      {children}
    </AppShell>
  )

  // Presentation only, as everywhere else: moderation_queue() checks
  // content.moderate in its own WHERE clause, so someone who typed this URL
  // gets an empty result from the database regardless of what this branch does.
  if (!canModerate) {
    return shell(
      <Card title="Moderation">
        <p className="empty">
          This section needs the Moderator role. If you think you should have
          it, ask a tenant administrator.
        </p>
      </Card>,
    )
  }

  return shell(
    <div className="moderation">
      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      {/* OPS-25. Stated on the screen where it has consequences rather than
          left in a document: until moderation_policy v2 carries written block
          criteria, the check flags and never blocks, so everything below has
          already published. A moderator who does not know that will read this
          queue as "things being held back". */}
      <p className="moderation__notice">
        The check <strong>flags but does not block</strong>. Every post here has
        already gone out unless it is marked <em>Held back</em>. Written block
        criteria are moderation policy v2 — OPS-25.
      </p>

      <Card
        title="Waiting for review"
        action={
          items ? (
            <span className="pill">
              {items.length} {items.length === 1 ? 'item' : 'items'}
            </span>
          ) : null
        }
      >
        {items === null && !error && (
          <p className="empty" aria-busy="true">
            Loading the queue…
          </p>
        )}

        {items && items.length === 0 && (
          <p className="empty">
            Nothing is waiting. Flagged and blocked posts appear here, oldest
            first.
          </p>
        )}
      </Card>

      {(items ?? []).map((item) => (
        <QueueItem
          key={item.verdictId}
          item={item}
          busy={busy === item.verdictId}
          onAct={(action, note) => act(item.verdictId, action, note)}
        />
      ))}
    </div>,
  )
}

/** Same rule as the hub's avatar: first letters of the first two words. */
function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}
